import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getMyIdentityConfirmationQueryKey,
  useGetMyIdentityConfirmation,
  usePostMyIdentityConfirmation,
} from '@eureca/candidate-api-sdk/hooks';
import { toast } from '@eureca/ui';
import { getKeycloak } from '@/auth/keycloak-instance';
import { logoutCandidate } from '@/auth/logout';
import { useMediaQuery } from '@/lib/use-media-query';
import {
  IdentityConfirmation as IdentityConfirmationComponent,
  type IdentityConfirmationError,
  type IdentityConfirmationMethod,
} from './IdentityConfirmation.component';
import {
  closeIdentityConfirmation,
  markIdentityConfirmed,
  useIdentityConfirmationStore,
} from './identity-confirmation-store';
import {
  PROVIDER_IDP_ALIAS,
  buildReturnUrl,
  savePending,
  type ReauthProvider,
} from './identity-reauth-return';

export const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

/** AC-36 — toast na página de destino, título por breakpoint, com "Fechar". */
export function notifyIdentityConfirmed(isDesktop: boolean): void {
  toast(isDesktop ? 'Identidade confirmada com sucesso' : 'Identidade confirmada', {
    action: { label: 'Fechar', onClick: () => toast.dismiss() },
  });
}

/** "redefina ela" (AC-26): o fluxo de redefinição de senha que já existe (página do Keycloak). */
export function resetPasswordUrl(): string {
  const kc = getKeycloak();
  const base = (kc.authServerUrl ?? '').replace(/\/+$/, '');
  return `${base}/realms/${kc.realm}/login-actions/reset-credentials?client_id=${kc.clientId}`;
}

/** Sai para o provedor com `prompt=login`; a ação pendente fica em sessionStorage (AC-30). */
export function startProviderReauth(
  provider: ReauthProvider,
  action: Parameters<typeof savePending>[0]['action'],
  originPath: string
): void {
  const kc = getKeycloak();
  const token = kc.tokenParsed as { sub?: string; auth_time?: number } | undefined;
  savePending({
    sub: token?.sub ?? '',
    provider,
    action,
    originPath,
    authTimeBefore: token?.auth_time ?? 0,
    startedAt: Date.now(),
  });
  void kc.login({
    prompt: 'login',
    idpHint: PROVIDER_IDP_ALIAS[provider],
    redirectUri: buildReturnUrl(window.location.origin, originPath),
  });
}

function mapMethod(
  data: { method: string } | undefined,
  isPending: boolean
): IdentityConfirmationMethod {
  if (!data) return isPending ? 'loading' : 'none';
  const m = data.method;
  return m === 'password' || m === 'google' || m === 'linkedin' ? m : 'none';
}

/** Container montado uma vez (`IdentityConfirmationHost`); o estado vem da store. */
export function IdentityConfirmation() {
  const { open, request, providerError } = useIdentityConfirmationStore();
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const queryClient = useQueryClient();
  const [error, setError] = useState<IdentityConfirmationError>(null);
  const [retryAfter, setRetryAfter] = useState(60);
  // AC-23 — guarda de reentrância síncrona: um clique duplo dispara dois cliques
  // antes de o React re-renderizar o botão em loading; `isPending` ainda é false.
  const inFlight = useRef(false);

  const state = useGetMyIdentityConfirmation({
    query: { enabled: open, staleTime: 0, refetchOnWindowFocus: false },
  });

  useEffect(() => {
    if (!open) setError(null);
  }, [open]);

  const mutation = usePostMyIdentityConfirmation({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getMyIdentityConfirmationQueryKey(), data);
        setError(null);
        notifyIdentityConfirmed(isDesktop);
        markIdentityConfirmed();
      },
      onError: (err) => {
        const e = err as {
          status?: number;
          body?: { error?: string; retryAfterSeconds?: number };
        };
        if (e.status === 429) {
          // SC-06 — senhas erradas seguidas: o servidor informa a espera.
          setRetryAfter(e.body?.retryAfterSeconds ?? 60);
          setError('too-many-attempts');
          return;
        }
        if (e.status === 422) {
          setError('invalid-password');
          return;
        }
        if (e.status === 403 && e.body?.error === 'PASSWORD_NOT_AVAILABLE') {
          void state.refetch();
          return;
        }
        // 503 VERIFICATION_UNAVAILABLE, 502, rede: erro distinto do de senha incorreta (AC-38).
        setError('unavailable');
      },
    },
  });

  const submitPassword = useCallback(
    (password: string) => {
      if (inFlight.current || mutation.isPending) return; // AC-23 — uma verificação por vez
      inFlight.current = true;
      setError(null);
      mutation.mutate(
        { data: { method: 'password', password } },
        {
          onSettled: () => {
            inFlight.current = false;
          },
        }
      );
    },
    [mutation]
  );

  const onProvider = useCallback(() => {
    const method = state.data?.method;
    if (!request || (method !== 'google' && method !== 'linkedin')) return;
    startProviderReauth(method, request.action, request.originPath);
  }, [request, state.data?.method]);

  const onLogout = useCallback(() => {
    // AC-33 — encerra a sessão; a ação pendente é descartada.
    closeIdentityConfirmation();
    logoutCandidate();
  }, []);

  const onResetPassword = useCallback(() => {
    // AC-26 — fecha, descarta a ação pendente e vai ao fluxo de redefinição.
    closeIdentityConfirmation();
    window.location.assign(resetPasswordUrl());
  }, []);

  return (
    <IdentityConfirmationComponent
      open={open}
      isDesktop={isDesktop}
      method={mapMethod(state.data, state.isPending)}
      providerError={providerError}
      error={error}
      retryAfterSeconds={retryAfter}
      loading={mutation.isPending}
      onSubmitPassword={submitPassword}
      onProvider={onProvider}
      onLogout={onLogout}
      onDismiss={closeIdentityConfirmation}
      onResetPassword={onResetPassword}
    />
  );
}
